import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBanner } from '@/components/StatusBanner';
import {
  deletePrompt,
  fetchPromptDefaults,
  listPrompts,
  patchPrompt,
  upsertPrompt,
  type AgentPromptRow,
  type PromptSlot,
} from '@/lib/agentPromptsApi';
import { canManageSystemPrompts } from '@/lib/roles';
import { useMe } from '@/lib/useMe';

type Slot = PromptSlot;

const GROUP_HEADINGS: Record<string, string> = { agent: 'Agent', series: 'Series' };

/** Slots grouped in the order the server listed them; a group the console does
 * not know keeps its raw name as the heading, nothing is dropped. */
function groupSlots(slots: readonly Slot[]): Array<{ group: string; heading: string; slots: Slot[] }> {
  const groups: Array<{ group: string; heading: string; slots: Slot[] }> = [];
  for (const slot of slots) {
    let entry = groups.find((g) => g.group === slot.group);
    if (!entry) {
      entry = { group: slot.group, heading: GROUP_HEADINGS[slot.group] ?? slot.group, slots: [] };
      groups.push(entry);
    }
    entry.slots.push(slot);
  }
  return groups;
}

/** null when `text` is a JSON object, else the reason it is not. */
function jsonObjectProblem(text: string): string | null {
  try {
    const value: unknown = JSON.parse(text);
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return 'This slot must be a JSON object ({ ... }).';
    }
    return null;
  } catch {
    return 'This is not valid JSON.';
  }
}

const characters = (text: string) => `${text.length.toLocaleString('en-US')} characters`;

const ROWS_KEY = ['admin', 'agent-prompts'] as const;
const DEFAULTS_KEY = ['admin', 'agent-prompts', 'defaults'] as const;

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** A switch built on a checkbox — this app has no switch component. */
function EnabledSwitch(props: { checked: boolean; label: string; onChange: (next: boolean) => void }) {
  return (
    <input
      type="checkbox"
      role="switch"
      aria-label={props.label}
      checked={props.checked}
      onChange={(event) => props.onChange(event.target.checked)}
      className="h-4 w-4 accent-primary"
    />
  );
}

/**
 * System prompts (moved here from the product's Settings -> Agent). SUPERADMIN
 * ONLY — the nav hides it and the API refuses every other console role; a
 * person who types the URL sees the refusal.
 *
 * One prompt per slot, in force for every model and every agent. A slot with no
 * override runs on the built-in prompt shipped in code (fetched by api-py from
 * agent-gateway). Saving text identical to the built-in default removes the
 * override instead of creating one.
 */
export function SystemPromptsPage() {
  const { data: me } = useMe();
  const allowed = canManageSystemPrompts(me?.role);
  const queryClient = useQueryClient();

  const rowsQuery = useQuery({
    queryKey: ROWS_KEY,
    queryFn: listPrompts,
    enabled: allowed,
    retry: false,
  });
  const defaultsQuery = useQuery({
    queryKey: DEFAULTS_KEY,
    queryFn: fetchPromptDefaults,
    enabled: allowed,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const [editing, setEditing] = useState<Slot | null>(null);
  const [content, setContent] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [resetTarget, setResetTarget] = useState<AgentPromptRow | null>(null);
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);

  const rows = rowsQuery.data ?? [];
  const slots: Slot[] | undefined = defaultsQuery.data?.slots;
  const bySlot = (key: string) => rows.find((row) => row.kind === 'system' && row.key === key);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ROWS_KEY, exact: true });

  const save = useMutation({
    mutationFn: async (slot: Slot) => {
      const existing = bySlot(slot.key);
      const matchesDefault = content.trim() === slot.default.trim();
      if (matchesDefault) {
        // Identical to the built-in default = no override needed. Remove an
        // existing one (acts as Reset); if none, create nothing and say so.
        if (existing) await deletePrompt(existing.id);
        return existing
          ? `${slot.label}: matches the default — override removed.`
          : `${slot.label}: already the built-in default — no override created.`;
      }
      await upsertPrompt({ kind: 'system', key: slot.key, title: slot.label, content, enabled });
      return `${slot.label} prompt saved.`;
    },
    onSuccess: (message) => {
      setNotice({ kind: 'success', message });
      setEditing(null);
      void refresh();
    },
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to save') }),
  });

  const toggle = useMutation({
    mutationFn: ({ row, next }: { row: AgentPromptRow; next: boolean }) =>
      patchPrompt(row.id, { enabled: next }),
    onSuccess: () => void refresh(),
    onError: (err) => setNotice({ kind: 'error', message: errorText(err, 'Failed to update') }),
  });

  const reset = useMutation({
    mutationFn: (row: AgentPromptRow) => deletePrompt(row.id),
    onSuccess: () => {
      setNotice({ kind: 'success', message: 'Override removed — the built-in default is back in use.' });
      setResetTarget(null);
      void refresh();
    },
    onError: (err) => {
      setResetTarget(null);
      setNotice({ kind: 'error', message: errorText(err, 'Failed to reset') });
    },
  });

  if (!allowed) {
    return (
      <div>
        <h1 className="text-card-title font-semibold text-fg-primary">System prompts</h1>
        <p className="mt-2 text-body text-fg-secondary">
          System prompts are for the superadmin role only.
        </p>
      </div>
    );
  }

  const openEdit = (slot: Slot) => {
    const row = bySlot(slot.key);
    setNotice(null);
    setEditing(slot);
    // Start from the current effective prompt so editing starts from real text.
    setContent(row?.content ?? slot.default);
    setEnabled(row?.enabled ?? true);
  };

  const loadDefault = () => {
    if (editing) setContent(editing.default);
  };

  const jsonProblem = editing?.format === 'json' && content.trim() ? jsonObjectProblem(content) : null;

  // Until the overrides are known the page makes NO claim about what is in use:
  // an empty list that is really "not loaded" would offer to overwrite an
  // override from the default text.
  const rowsReady = rowsQuery.isSuccess;

  return (
    <div>
      <h1 className="text-card-title font-semibold text-fg-primary">System prompts</h1>
      <p className="mt-1 max-w-2xl text-body text-fg-secondary">
        You can fully replace any of these prompts with your own — hit Override (they run on the
        built-in default until you do). They are fixed slots the agent always needs, so you cannot
        delete them; Reset drops your override and restores the default.
      </p>

      <div className="mt-4 space-y-3">
        {notice && !editing && <StatusBanner kind={notice.kind} message={notice.message} />}
        {rowsQuery.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBanner
              kind="error"
              message={`Could not load the overrides: ${errorText(rowsQuery.error, 'unknown error')}`}
            />
            <button
              type="button"
              onClick={() => void rowsQuery.refetch()}
              disabled={rowsQuery.isFetching}
              className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
            >
              {rowsQuery.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}
        {defaultsQuery.isError && (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBanner
              kind="error"
              message={`Could not load the built-in defaults: ${errorText(defaultsQuery.error, 'unknown error')}`}
            />
            <button
              type="button"
              onClick={() => void defaultsQuery.refetch()}
              disabled={defaultsQuery.isFetching}
              className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
            >
              {defaultsQuery.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {defaultsQuery.isPending && <p className="text-body text-fg-hint">Loading prompts…</p>}

        {groupSlots(slots ?? []).map((g) => (
          <div key={g.group} className="space-y-3">
            <h2 className="pt-2 text-h3 font-semibold text-fg-primary">
              {g.heading} ({g.slots.length})
            </h2>
            {g.slots.map((slot) => {
              const row = bySlot(slot.key);
              let preview: string;
              if (!rowsReady) preview = rowsQuery.isError ? '(overrides unavailable)' : '(loading overrides…)';
              else if (row) preview = row.content;
              else preview = slot.default;
              return (
                <section key={slot.key} className="rounded-card border border-border bg-surface-field p-4">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-body font-medium text-fg-primary">{slot.label}</h3>
                        <span className="rounded-full bg-surface-brand-subtle px-2 py-0.5 text-micro font-medium text-fg-secondary">
                          {!rowsReady ? 'Unknown' : row ? (row.enabled ? 'Override' : 'Override (disabled)') : 'Built-in default'}
                        </span>
                      </div>
                      <p className="mt-1 text-meta text-fg-secondary">{slot.hint}</p>
                      <p className="mt-2 line-clamp-3 whitespace-pre-wrap rounded-card bg-state-hover p-2 font-mono text-micro text-fg-secondary">
                        {preview}
                      </p>
                      {rowsReady && (
                        <p className="mt-1 text-micro text-fg-hint">
                          Currently in use: {row ? 'current override' : 'built-in default'}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {!rowsReady ? (
                        <button type="button" disabled className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary opacity-50">
                          Override
                        </button>
                      ) : row ? (
                        <>
                          <EnabledSwitch
                            checked={row.enabled}
                            label={`${slot.label} override enabled`}
                            onChange={(next) => toggle.mutate({ row, next })}
                          />
                          <button
                            type="button"
                            onClick={() => openEdit(slot)}
                            className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setResetTarget(row)}
                            className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-destructive-soft-foreground hover:bg-state-hover"
                          >
                            Reset
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openEdit(slot)}
                          className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
                        >
                          Override
                        </button>
                      )}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        ))}
      </div>

      {editing && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Edit ${editing.label} prompt`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
        >
          <div className="flex max-h-full w-full max-w-3xl flex-col rounded-card border border-border bg-surface-field p-6">
            <h2 className="text-h3 font-semibold text-fg-primary">Edit {editing.label} prompt</h2>
            <p className="mt-1 text-meta text-fg-secondary">{editing.hint}</p>
            <div className="mt-4 flex items-center justify-between gap-2">
              <label htmlFor="prompt-content" className="text-meta font-medium text-fg-secondary">
                Prompt content
              </label>
              <button
                type="button"
                onClick={loadDefault}
                disabled={save.isPending}
                className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
              >
                Load built-in default
              </button>
            </div>
            <textarea
              id="prompt-content"
              value={content}
              rows={16}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Leave empty and Reset to fall back to the built-in default."
              className="mt-2 min-h-0 w-full flex-1 rounded-card border border-border bg-background p-2 font-mono text-micro text-fg-primary"
            />
            {editing.format === 'json' && (
              <p className="mt-2 text-meta text-fg-secondary">
                This slot holds a JSON object. Only valid JSON objects can be saved; the shape of the
                question itself is not checked here, and the gateway quietly falls back to the
                built-in question if it rejects it.
              </p>
            )}
            {jsonProblem && (
              <p role="alert" className="mt-1 text-meta text-destructive-soft-foreground">
                {jsonProblem}
              </p>
            )}
            <label className="mt-3 flex items-center gap-2 text-body text-fg-primary">
              <EnabledSwitch checked={enabled} label="Enabled" onChange={setEnabled} />
              Enabled
            </label>
            {notice?.kind === 'error' && (
              <div className="mt-4">
                <StatusBanner kind="error" message={notice.message} />
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                disabled={save.isPending}
                className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => save.mutate(editing)}
                disabled={save.isPending || !content.trim() || jsonProblem !== null}
                className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <ConfirmDialog
          title="Reset to built-in default"
          description={`Override: ${characters(resetTarget.content)}. Built-in: ${characters(
            slots?.find((slot) => slot.key === resetTarget.key)?.default ?? '',
          )}. The agent switches to the built-in text immediately. This row may be maintained by migrations, so removing it can undo a deliberate change. Remove this override?`}
          confirmLabel="Reset"
          busy={reset.isPending}
          onConfirm={() => reset.mutate(resetTarget)}
          onCancel={() => setResetTarget(null)}
        />
      )}
    </div>
  );
}
